import { api } from '../api/client';

// Shared TTS engine (Alex 2026-06-20: per-bubble 🔊 in the group chat
// reuses the exact same speak rules as the agent composer — native Siri /
// OpenAI compact / OpenAI prose — but for a KNOWN piece of text instead of
// "everything since my last message").
//
// The composer's InputBar still owns its richer flow (conversation-bundle
// extraction, F5-persistence, pause/rewind). This module is the minimal
// "speak this exact string, stop on demand" core that a bubble button — or
// later anything else — can call. Mode is read from the same
// `cloud-tts-mode` localStorage key, so flipping the mode in the composer
// menu changes the bubble speakers too.

export type TtsMode = 'native' | 'openai-compact' | 'openai-prose';

export function getTtsMode(): TtsMode {
  const raw = localStorage.getItem('cloud-tts-mode');
  if (raw === 'openai-prose') return 'openai-prose';
  if (raw === 'openai-compact') return 'openai-compact';
  return 'native';
}

// Strip markdown/code/links so the engine reads prose, not syntax.
// Mirrors the composer's native-TTS cleanup.
function toSpeakable(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`[^`]*`/g, '')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/[#*_~>|]/g, '')
    .replace(/^\s*[-•]\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// ── Singleton playback state ─────────────────────────────────────────
// Only one thing speaks at a time across the whole app. A new speak()
// cancels whatever was playing. Listeners (the bubble buttons) subscribe
// to know which id is currently active so they can flip 🔊 ↔ ◼.
let activeId: string | null = null;
let activeAudio: HTMLAudioElement | null = null;
let activePaused = false;    // is activeAudio paused? (OpenAI only)
let activeSeekable = false;  // true only once real playback started → pause/seek possible (#206)
let activeLoading = false;   // fetching TTS audio, not yet playing → show a spinner, no controls
// Elapsed-seconds ticker for the loading state. `prose` synthesis is pipeline-
// bound (GPT summary → TTS) and legitimately takes 30-50s; a silent spinner for
// that long reads as "hung" (Alex 2026-07-17). We tick a counter every second
// while loading so the UI can show "TTS · 14s" — the wait becomes legible.
let activeLoadStart = 0;
let loadTicker: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function startLoadTicker() {
  activeLoadStart = Date.now();
  if (loadTicker) clearInterval(loadTicker);
  loadTicker = setInterval(() => { if (activeLoading) emit(); }, 1000);
}
function stopLoadTicker() {
  if (loadTicker) { clearInterval(loadTicker); loadTicker = null; }
  activeLoadStart = 0;
}

function emit() {
  for (const l of listeners) l();
}

export function subscribeTts(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSpeakingId(): string | null {
  return activeId;
}

// ── Playback controls (OpenAI HTMLAudioElement only; native Web-Speech isn't
//    seekable). Powers the per-section ChatGPT-style pause + ±15s (Alex #206). ──
export function getTtsPaused(): boolean { return activePaused; }
export function getTtsSeekable(): boolean { return activeSeekable; }
export function getTtsLoading(): boolean { return activeLoading; }
// Seconds since the current loading spinner started (0 when not loading).
export function getTtsElapsed(): number {
  return activeLoadStart ? Math.floor((Date.now() - activeLoadStart) / 1000) : 0;
}

export function pauseResumeTts(): void {
  if (!activeAudio) return;
  if (activeAudio.paused) { void activeAudio.play().catch(() => {}); activePaused = false; }
  else { activeAudio.pause(); activePaused = true; }
  emit();
}

export function seekTts(seconds: number): void {
  if (!activeAudio) return;
  let t = activeAudio.currentTime + seconds;
  if (t < 0) t = 0;
  const dur = activeAudio.duration;
  if (Number.isFinite(dur) && t > dur) t = dur;
  try { activeAudio.currentTime = t; } catch { /* not seekable yet */ }
}

export function stopTts(): void {
  try { window.speechSynthesis?.cancel(); } catch { /* no engine */ }
  if (activeAudio) {
    try { activeAudio.pause(); activeAudio.currentTime = 0; } catch { /* detached */ }
    activeAudio = null;
  }
  activePaused = false;
  activeSeekable = false;
  activeLoading = false;
  stopLoadTicker();
  if (activeId !== null) {
    activeId = null;
    emit();
  }
}

function setActive(id: string | null) {
  activeId = id;
  if (id === null) { activePaused = false; activeSeekable = false; activeLoading = false; stopLoadTicker(); }
  emit();
}

// Native Web-Speech / Siri. Must be reachable inside the click gesture on
// iOS — we fire a silent primer synchronously, then queue the real one.
function speakNative(id: string, text: string) {
  try {
    const primer = new SpeechSynthesisUtterance(' ');
    primer.volume = 0;
    primer.lang = 'de-DE';
    window.speechSynthesis.speak(primer);
  } catch { /* engine missing */ }

  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text.slice(0, 1500));
  u.lang = 'de-DE';
  const apply = () => {
    const voices = window.speechSynthesis.getVoices();
    const de = voices.find((v) => v.lang.startsWith('de'));
    if (de) u.voice = de;
    u.onstart = () => setActive(id);
    u.onend = () => { if (activeId === id) setActive(null); };
    u.onerror = () => { if (activeId === id) setActive(null); };
    window.speechSynthesis.speak(u);
  };
  if (window.speechSynthesis.getVoices().length === 0) {
    const done = () => apply();
    const timer = setTimeout(done, 800);
    window.speechSynthesis.addEventListener(
      'voiceschanged',
      () => { clearTimeout(timer); done(); },
      { once: true },
    );
  } else {
    apply();
  }
}

// Server-side OpenAI TTS (compact = driving summary, prose = narration).
// Same /api/tts-text contract the composer uses. The silent-audio unlock
// must run synchronously in the gesture (iOS), so we create + play it
// before the async fetch.
async function speakOpenAI(id: string, text: string, style: 'compact' | 'prose') {
  const audio = new Audio();
  // 1-frame silent mp3 to unlock the audio element inside the gesture.
  audio.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAABhgHkbKCAAAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAABhgHkbKCAAAAAAAAAAAAAAAAA';
  audio.play().catch(() => {});
  activeAudio = audio;
  // We're FETCHING the audio, not playing yet — spinner, NOT the transport controls
  // (Alex #206 follow-up: controls appeared during the fetch). Seekable/pause only
  // switch on once real playback starts (audio.onplaying, below).
  activeLoading = true;
  activeSeekable = false;
  activePaused = false;
  startLoadTicker();
  setActive(id);

  // Fail VISIBLY instead of spinning forever if /api/tts-text hangs (Alex: "2 min,
  // nichts, Button bleibt rosa") — but not so eagerly that a legit synthesis dies
  // (Alex 2026-07-17: "Prosa TTS … Spinner, dann hört es auf"). The latency is
  // PIPELINE-bound, not input-bound: the backend GPT-summarizes prose (>250 chars)
  // into 150-300 words BEFORE any TTS, which alone runs 30-50s regardless of how
  // short the input is. So a length-scaled budget under-shoots short prose. Give
  // prose a high fixed floor (90s) plus headroom for long inputs; compact only
  // summarizes >400 chars and stays 2-3 sentences → the 45s floor covers it.
  const floor = style === 'prose' ? 90_000 : 45_000;
  const ctrl = new AbortController();
  const timeoutMs = Math.min(180_000, floor + text.length * 15);
  const abortTimer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await api.ttsText({ text, voice: 'nova', lang: 'de', style }, ctrl.signal);
    clearTimeout(abortTimer);
    if (!res.ok) { if (activeId === id) setActive(null); return; }
    const blob = await res.blob();
    if (blob.size < 100) { if (activeId === id) setActive(null); return; }
    // A newer speak() may have superseded us while the fetch was in flight.
    if (activeId !== id) return;
    const url = URL.createObjectURL(blob);
    audio.src = url;
    // Real playback started → drop the spinner, enable pause/seek (#206).
    audio.onplaying = () => { if (activeId === id) { activeLoading = false; activeSeekable = true; emit(); } };
    audio.onended = () => { URL.revokeObjectURL(url); if (activeId === id) setActive(null); };
    audio.play().catch(() => {
      // Autoplay blocked → play on next user click.
      const retry = () => { audio.play().catch(() => {}); };
      document.addEventListener('click', retry, { once: true });
    });
  } catch {
    clearTimeout(abortTimer);
    if (activeId === id) setActive(null);
  }
}

/**
 * Speak `rawText` under `id`. Calling with the id that's already speaking
 * is the caller's cue to stop (they check getSpeakingId first). A new id
 * cancels whatever was playing. Must be invoked from a user gesture.
 */
export function speakTts(id: string, rawText: string): void {
  stopTts();
  const text = toSpeakable(rawText);
  if (!text) return;
  const mode = getTtsMode();
  if (mode === 'openai-compact') void speakOpenAI(id, text, 'compact');
  else if (mode === 'openai-prose') void speakOpenAI(id, text, 'prose');
  else speakNative(id, text);
}
